import React, { useState } from "react";
import { toast } from "react-toastify";
import * as expenseService from "../../services/expenseService";
import SlidePanel from "../../components/SlidePanel/SlidePanel";
import Loader from "../../components/Loader/Loader";
import ExpenseDatePicker from "./ExpenseDatePicker";
import { labelStyle, inputStyle, SERVICE_FLAGS } from "./expenseStyles";
import { BLUE_DREAM, JC_ALQURE, resolveTemplate } from "./expenseTemplates";

const FORM_ID = "expense-entry-form";
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// Edit form for one not-yet-invoiced entry. Mounted only while an entry is
// being edited (see ExpensesGrid), so its state starts fresh each time. The
// fields follow the branch's upload template (entry.upload_template):
// STANDARD is the original form; BLUE_DREAM has no trucking date / services and
// calls the pre-VAT figure "Amount"; JC_ALQURE edits Description, Price and Qty
// (Amount = Price x Qty).
function EntryForm({ entry, onSaved, onSavingChange }) {
  const template = resolveTemplate(entry.upload_template);
  const isJc = template === JC_ALQURE;
  const isBlue = template === BLUE_DREAM;
  const [form, setForm] = useState({
    job_schedule_date: entry.job_schedule_date || "",
    trucking_date: entry.trucking_date || "",
    vin_number: entry.vin_number || "",
    model: entry.model || "",
    classification: entry.classification || "",
    washing: Number(entry.washing) === 1,
    polishing: Number(entry.polishing) === 1,
    pdi: Number(entry.pdi) === 1,
    valeting: Number(entry.valeting) === 1,
    net_amount: String(entry.net_amount ?? ""),
    description: entry.description || "",
    unit_price: String(entry.unit_price ?? entry.net_amount ?? ""),
    qty: String(Number(entry.qty ?? 1)),
    vat_pct: String(entry.vat_pct ?? "5"),
  });
  const [errors, setErrors] = useState({});

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };
  const setFlag = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.checked }));
  const setDate = (key) => (value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const price = Number(form.unit_price);
  const qtyNum = Number(form.qty);
  const jcInputsOk = form.unit_price !== "" && Number.isFinite(price) && price >= 0 && form.qty !== "" && Number.isFinite(qtyNum) && qtyNum > 0;
  const net = isJc ? (jcInputsOk ? round2(price * qtyNum) : NaN) : Number(form.net_amount);
  const pct = Number(form.vat_pct);
  const previewOk = (isJc ? jcInputsOk : form.net_amount !== "" && Number.isFinite(net) && net >= 0) && Number.isFinite(pct);
  const vatAmount = previewOk ? round2((net * pct) / 100) : 0;
  const total = previewOk ? round2(net + vatAmount) : 0;

  const validate = () => {
    const errs = {};
    if (!form.vin_number.trim()) errs.vin_number = isJc ? "Vehicle number is required" : "VIN number is required";
    if (!form.job_schedule_date) errs.job_schedule_date = isJc ? "Date is required" : "Job schedule date is required";
    if (isJc) {
      if (!form.description.trim()) errs.description = "Description is required";
      if (form.unit_price === "" || !Number.isFinite(price) || price < 0) errs.unit_price = "Enter a valid price";
      if (form.qty === "" || !Number.isFinite(qtyNum) || qtyNum <= 0) errs.qty = "Enter a quantity above 0";
    } else if (form.net_amount === "" || !Number.isFinite(net) || net < 0) {
      errs.net_amount = "Enter a valid amount";
    }
    if (form.vat_pct === "" || !Number.isFinite(pct) || pct < 0 || pct > 100) errs.vat_pct = "Enter 0 to 100";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      onSavingChange(true);
      const res = await expenseService.updateEntry(entry.id, {
        ...form,
        vin_number: form.vin_number.trim(),
        trucking_date: form.trucking_date || null,
        net_amount: net,
        vat_pct: pct,
        // Price / Qty / Description belong to the JC_ALQURE template only; leave them out otherwise.
        ...(isJc ? { description: form.description.trim(), unit_price: price, qty: qtyNum } : { description: undefined, unit_price: undefined, qty: undefined }),
      });
      if (res?.success) {
        toast.success(res.message || "Saved");
        onSaved();
      } else {
        toast.error(expenseService.responseMessage(res));
      }
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      onSavingChange(false);
    }
  };

  const dateField = (key, label, props = {}) => (
    <div className="mb-3">
      <label htmlFor={`entry-${key}`} style={labelStyle}>{label}</label>
      <ExpenseDatePicker id={`entry-${key}`} value={form[key]} onChange={setDate(key)} invalid={!!errors[key]} {...props} />
      {errors[key] && <div className="invalid-feedback d-block">{errors[key]}</div>}
    </div>
  );

  const input = (key, label, props = {}) => (
    <div className="mb-3">
      <label htmlFor={`entry-${key}`} style={labelStyle}>{label}</label>
      <input
        id={`entry-${key}`}
        className={`form-control${errors[key] ? " is-invalid" : ""}`}
        style={inputStyle}
        value={form[key]}
        onChange={set(key)}
        {...props}
      />
      {errors[key] && <div className="invalid-feedback">{errors[key]}</div>}
    </div>
  );

  return (
    <form id={FORM_ID} onSubmit={onSubmit} noValidate>
      {isJc ? (
        <>
          <div className="row">
            <div className="col-6">{dateField("job_schedule_date", "Date *")}</div>
            <div className="col-6">{input("vin_number", "Vehicle Number *")}</div>
          </div>
          {input("description", "Description *", { maxLength: 255 })}
          {input("model", "Model")}
          <div className="row">
            <div className="col-4">{input("unit_price", "Price *", { type: "number", min: 0, step: "0.01" })}</div>
            <div className="col-4">{input("qty", "Qty *", { type: "number", min: 0, step: "any" })}</div>
            <div className="col-4">{input("vat_pct", "VAT % *", { type: "number", min: 0, max: 100, step: "0.01" })}</div>
          </div>
        </>
      ) : (
        <>
          <div className="row">
            <div className="col-6">{dateField("job_schedule_date", "Job Schedule Date *")}</div>
            {!isBlue && <div className="col-6">{dateField("trucking_date", "Trucking Date", { isClearable: true })}</div>}
          </div>
          {input("vin_number", "VIN Number *")}
          <div className="row">
            <div className="col-6">{input("model", "Model")}</div>
            <div className="col-6">{input("classification", "Classification")}</div>
          </div>

          {!isBlue && (
            <div className="mb-3">
              <label style={labelStyle}>Services</label>
              <div className="d-flex flex-wrap gap-3">
                {SERVICE_FLAGS.map(([key, label]) => (
                  <label key={key} className="d-flex align-items-center gap-2" style={{ fontSize: "14px", cursor: "pointer" }}>
                    <input type="checkbox" checked={form[key]} onChange={setFlag(key)} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="row">
            <div className="col-6">{input("net_amount", isBlue ? "Amount *" : "Net Amount *", { type: "number", min: 0, step: "0.01" })}</div>
            <div className="col-6">{input("vat_pct", "VAT % *", { type: "number", min: 0, max: 100, step: "0.01" })}</div>
          </div>
        </>
      )}

      <div style={{ background: "#F9FAFB", borderRadius: "8px", padding: "12px 16px", fontSize: "14px", color: "#374151" }}>
        {isJc && (
          <div className="d-flex justify-content-between mb-1">
            <span>Amount (Price x Qty)</span>
            <strong>{(previewOk ? net : 0).toFixed(2)}</strong>
          </div>
        )}
        <div className="d-flex justify-content-between">
          <span>VAT amount</span>
          <strong>{vatAmount.toFixed(2)}</strong>
        </div>
        <div className="d-flex justify-content-between mt-1">
          <span>{isBlue ? "Net Amount" : "Total"}</span>
          <strong>{total.toFixed(2)}</strong>
        </div>
        <div style={{ fontSize: "12px", color: "#6B7280", marginTop: 6 }}>
          Recalculated from {isJc ? "Price, Qty" : isBlue ? "Amount" : "Net Amount"} and VAT % when saved.
        </div>
      </div>
    </form>
  );
}

function ExpenseEntryDrawer({ entry, onHide, onSaved }) {
  const [saving, setSaving] = useState(false);
  const close = () => {
    if (!saving) onHide();
  };

  return (
    <SlidePanel
      show={!!entry}
      onHide={close}
      title="Edit Expense Entry"
      footer={
        <>
          <button className="btn btn-outline-secondary px-4" onClick={close} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={FORM_ID} className="btn Button primaryBackground text-white px-4" disabled={saving}>
            {saving ? <Loader style={{ width: 40, height: 20 }} /> : "Save"}
          </button>
        </>
      }
    >
      {entry && <EntryForm key={entry.id} entry={entry} onSaved={onSaved} onSavingChange={setSaving} />}
    </SlidePanel>
  );
}

export default ExpenseEntryDrawer;
